import type { DailyMetricsFreshnessResponse } from "@voice2mail/shared/v2m_api";

import { MetricCard } from "./MetricCard";

interface DailyMetricsFreshnessProps {
  data: DailyMetricsFreshnessResponse;
}

/**
 * V2M-DEV-163: `GET /admin/metrics/daily-metrics-freshness` existed with zero
 * panel consumers (grep in `src/api/client.ts` found not even a wrapper
 * function). This endpoint's entire purpose IS the "measured zero vs no
 * measurement" signal — it mirrors the scheduled watchdog job
 * (`src.jobs.daily_metrics_freshness_watchdog`) live and read-only, so the
 * panel can see "is the whole measurement layer even alive" independent of
 * whether that Cloud Scheduler trigger has been provisioned.
 *
 * `hoursSinceLastWrite === null` means the `daily_metrics` collection never
 * received a single write inside the lookback window — the loudest possible
 * finding, and deliberately NOT rendered as "0h" (a real, fresh write). See
 * backend `DailyMetricsFreshnessResponse` docstring for the full contract.
 */
export function DailyMetricsFreshness({ data }: DailyMetricsFreshnessProps) {
  const neverWritten = data.hoursSinceLastWrite === null;

  return (
    <div className="space-y-3">
      {neverWritten ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
          <div className="text-red-800 text-sm font-medium">
            NIGDY nie zapisano do <code>daily_metrics</code> w ostatnie {data.lookbackDays}{" "}
            {data.lookbackDays === 1 ? "dobę" : "dni"} — to nie jest zero, to brak pomiaru.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:max-w-2xl">
          <MetricCard
            label="Godzin od ostatniego zapisu"
            value={data.hoursSinceLastWrite!.toFixed(1)}
            warn={data.alerted}
          />
          <MetricCard label="Dni z zapisem w oknie" value={data.daysWithAnyDoc} />
          <MetricCard label="Próg ostrzeżenia (h)" value={data.alertThresholdHours} />
        </div>
      )}
      {data.alerted && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3" role="alert">
          <div className="text-amber-800 text-sm font-medium">
            ALERT: świeżość przekracza próg {data.alertThresholdHours}h.
          </div>
        </div>
      )}
      <p className="text-xs text-slate-400">Sprawdzone: {data.checkedAt}</p>
    </div>
  );
}
